import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/store';
import { getCurrentCustomerId, getCurrentCustomerDetails } from 'src/app/customers/state/customersSelector';
import DataTable from 'src/shared/components/data-table/DataTable';
import { DataTableColumns, DataTableState, TableCellRendererParams, TableRowData } from 'src/shared/components/data-table/dataTableTypes';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import Loader from 'src/shared/components/loader/Loader';
import { PrimaryButton } from 'src/shared/components/button/Button';
import Input from 'src/shared/components/input/Input';
import { SurveyDesign, Status } from 'src/shared/types';
import StatusIndicator from 'src/shared/components/status-indicator/StatusIndicator';
import { setCurrentProjectId } from 'src/app/projects/views/project-list/state/projectListSlice';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import { Plus as AddIcon, Search as SearchIcon } from 'lucide-react';
import styles from './SurveyAdmin.module.scss';
import { setFormState as setNewSurveyDesignFormState } from '../new-survey-design/state/newSurveyDesignSlice';
import { useSurveyDesignsQuery } from '../../state/api/surveyGraphSlice';
interface NavigationStateProps {
    sourcePage: string;
}

const getSurveyDesignStatusFromEnum = (status: string) => {
    switch (status) {
        case 'COMPLETED':
            return Status.SUCCESS;
        case 'PENDING':
            return Status.WARNING;
        default:
            return Status.UNKNOWN;
    }
};

const columns: DataTableColumns = [
    {
        columnId: 'projectId',
        title: 'Id',
        width: '0',
    },
    {
        columnId: 'projectName',
        title: 'Name',
        width: '15%',
    },
    {
        columnId: 'country',
        title: 'Sampling country',
        width: '15%',
    },
    {
        columnId: 'subscriptionType',
        title: 'Subscription type',
        width: '150px',
    },
    {
        columnId: 'status',
        title: 'Survey design status',
        width: '15%',
        renderer: (params: TableCellRendererParams) => {
            const status = params.cellData as string;
            const createdAt = params.rowData[5] as string;
            const statusText = status === 'PENDING' ? 'Incomplete' : 'Completed';

            return (
                <div className={styles.statusCell}>
                    <StatusIndicator status={getSurveyDesignStatusFromEnum(status)} statusText={statusText} />
                    <p>Expires at {moment(createdAt).add(5, 'days').format('DD MMM, HH:mm A')}</p>
                </div>
            );
        },
    },
    {
        columnId: 'created',
        title: 'Created',
        width: '20%',
    },
];

const SurveyAdmin = () => {
    const customerId = useAppSelector(getCurrentCustomerId) || '';
    const currentCustomerDetails = useAppSelector(getCurrentCustomerDetails);
    const title = 'Survey Admin';
    const subTitle = 'Manage my survey designs';
    const navigate = useAppNavigation();
    const dispatch = useAppDispatch();
    const [tableState, setTableState] = useState<Partial<DataTableState>>({
        fixedHeader: true,
        hiddenColumnIds: new Set(['projectId']),
        rowsPerPage: 10,
    });
    const [searchQuery, setSearchQuery] = useState<string>('');
    const location = useLocation();
    const navigationState = location.state as NavigationStateProps;

    const { isFetching, currentData, error } = useSurveyDesignsQuery(
        {
            customerId,
        },
        {
            skip: !customerId,
        }
    );

    useEffect(() => {
        dispatch(setCurrentProjectId(null));
    }, []);

    useEffect(() => {
        if (!currentData) {
            return;
        }

        const tableData: DataTableState['data'] = [];
        currentData.listSurveyDesigns.forEach((survery: SurveyDesign) => {
            const { projectName, country, createdAt, creatorName, projectId, status, subscription } = survery;

            if (!!projectName) {
                const currentRow = [];
                currentRow.push(projectId);
                currentRow.push(projectName);
                currentRow.push(country);
                currentRow.push(
                    subscription?.name === 'Survey_HabitatInsights' ? 'Survey design + Habitat Insights' : 'Survey design only'
                );
                currentRow.push(status);
                currentRow.push(
                    <>
                        <p>{moment(createdAt).format('DD MMM YYYY')}</p>
                        <p className={styles.lightText}>By {creatorName}</p>
                    </>
                );

                tableData.push(currentRow);
            }
        });

        setTableState({
            ...tableState,
            data: tableData,
        });
    }, [currentData, navigationState?.sourcePage]);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const queryString = e.target.value;
        setSearchQuery(queryString);
        setTableState({
            ...tableState,
            searchText: queryString,
        });
    };

    const onRowSelect = (rowData: TableRowData) => {
        const [projectId, projectName, , , status] = rowData;

        if (status === 'PENDING') {
            navigate.toNewSurveyDesign();
            setTimeout(() => {
                dispatch(
                    setNewSurveyDesignFormState({
                        projectId: projectId as string,
                        projectName: projectName as string,
                    })
                );
            }, 10);
            return;
        }

        dispatch(setCurrentProjectId(projectId as string));
        setTimeout(() => {
            navigate.toSurveyDesignSummary();
        }, 1);
    };

    if (isFetching) {
        return <Loader />;
    }

    if (error) {
        throw {
            error: true,
            message: 'Unable to fetch the survey designs.',
        };
    }

    if (!tableState.data) {
        return null;
    }

    const createSurveyDesignButton = (
        <PrimaryButton onClick={navigate.toNewSurveyDesign}>
            Create new survey design &nbsp; <AddIcon />
        </PrimaryButton>
    );

    const customerDetails = (
        <div className={styles.customerInfo}>
            <div className={styles.header}>Customer:</div>
            <div className={styles.name} data-testid='customer-name'>
                {currentCustomerDetails?.customerName}
            </div>
        </div>
    );

    if (!currentData?.listSurveyDesigns?.length) {
        const content = (
            <div className={styles.noDataContainer}>
                <div className={styles.content}>
                    <div>
                        <h4>No survey designs</h4>
                    </div>
                    <div>
                        <span>You have not created any survey designs for this customer</span>
                    </div>
                    <div>{createSurveyDesignButton}</div>
                </div>
            </div>
        );
        return <StandardAppLayout mainContent={content} title={title} subTitle={subTitle} otherActions={customerDetails} />;
    }

    const mainContent = (
        <div>
            <div className={styles.container}>
                <div className={styles.searchBar}>
                    <div className={styles.searchInputContainer}>
                        <span className={styles.searchIcon}>
                            <SearchIcon />
                        </span>
                        <Input
                            className={styles.searchInput}
                            value={searchQuery}
                            onChange={handleSearch}
                            placeholder='Search survey designs'
                        />
                    </div>
                </div>
                <div className={styles.button}>{createSurveyDesignButton}</div>
            </div>
            <div>
                <DataTable
                    state={tableState}
                    columns={columns}
                    onRowSelect={onRowSelect}
                    emptyStateMessage='There were no survey designs found matching this search. Adjust the search term to find survey designs for this customer.'
                />
            </div>
        </div>
    );

    return <StandardAppLayout mainContent={mainContent} title={title} subTitle={subTitle} otherActions={customerDetails} />;
};

export default SurveyAdmin;
